import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Grid from '@material-ui/core/Grid';
import GS from '../../grafsync/grafsync';
import TextField from '@material-ui/core/TextField';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React from 'react';
import CancelButton from '../CancelButton';
import PrimaryButton from '../PrimaryButton';
import { globalStyles as styles } from '../style/gstheme';
import { MODULES } from '../utils/constants';
import TitledPanel from '../TitledPanel';
import { isModuleActivated } from '../utils/utils';

// Note some of these values are set in the subsciptionUpdateSchema
const MIN_QUERY_TIMEOUT = 1;
const MAX_QUERY_TIMEOUT = 10800;
const MIN_IMPORT_SIZE = 1;
const MAX_IMPORT_SIZE = 10000;
const MIN_TRIPLES = 100000;
const MAX_TRIPLES = 100000000;
const MAX_USERS = 1000;
// This default is also in SubscriptionManager
const MIN_DOWNLOAD_TIMEOUT = 10;
const MAX_DOWNLOAD_TIMEOUT = 10800;

class UpdateSubscriptionDialog extends React.Component {


    constructor(props) {
        super(props);
        const subscriptionType = props.subscription.subscriptionType;
        const subscriptionOptions = GS.subscriptionOptions();
        // Get default subscription options first
        const maxTriplesAllowed = subscriptionOptions.find(option => option.subscriptionType === subscriptionType).maxTriples;
        const maxImportSize = subscriptionOptions.find(option => option.subscriptionType === subscriptionType).maxImportSize;
        const queryTimeout = subscriptionOptions.find(option => option.subscriptionType === subscriptionType).maxQueryTime;
        const modules = "module" in props.subscription ? props.subscription.module : 0;
        const treatContentAs = isModuleActivated(modules, MODULES.TREAT_CONTENT_AS);
        const dataManager = isModuleActivated(modules, MODULES.DATA_MANAGER);
        // We might override the default with admin managed values
        this.state = {
            subscriptionVersion: "subscriptionVersion" in props.subscription ?
                props.subscription.subscriptionVersion
                :
                0,
            maxUsers: props.subscription.maxUsers || 0,
            maxTriples: props.subscription.maxTriples || maxTriplesAllowed,
            maxImportSize: props.subscription.maxImportSize || maxImportSize,
            queryTimeout: props.subscription.queryTimeout || queryTimeout,
            downloadTimeout: props.subscription.downloadTimeout || 600,
            treatContentAs,
            dataManager
        };
    }

    updateSubscription = () => {
        const { maxUsers, maxTriples, maxImportSize, queryTimeout, downloadTimeout, subscriptionVersion, treatContentAs, dataManager } = this.state;
        let module = 0;
        if (treatContentAs) module += 1;
        if (dataManager) module += 2;
        this.props.onUpdate({
            maxUsers,
            maxTriples,
            maxImportSize,
            queryTimeout,
            downloadTimeout,
            subscriptionVersion,
            module
        });
    };

    exitUpdateSubscription = () => this.props.onCancel();

    handleMaxUsersChange = (event) => {
        const t = event.target;
        let value = t.value !== "" ? parseInt(t.value) : 0;
        this.setState({ maxUsers: value });
    };

    handleMaxTriplesChange = (event) => {
        const t = event.target;
        let value = t.value !== "" ? parseInt(t.value) : 0;
        this.setState({
            maxTriples: value
        });
    };

    handleMaxImportSizeChange = (event) => {
        const t = event.target;
        let value = t.value !== "" ? parseInt(t.value) : 0;
        this.setState({
            maxImportSize: value
        });
    };

    handleQueryTimeoutChange = (event) => {
        const t = event.target;
        let value = t.value !== "" ? parseInt(t.value) : 0;
        this.setState({
            queryTimeout: value
        });
    };

    handleDownloadTimeoutChange = (event) => {
        const t = event.target;
        let value = t.value !== "" ? parseInt(t.value) : 0;
        this.setState({
            downloadTimeout: value
        });
    };

    handleVersionChange = (event) => {
        const t = event.target;
        let value = t.value !== "" ? parseInt(t.value) : 0;
        if (value > 5) value = 5;
        this.setState({
            subscriptionVersion: value
        });
    };

    updateModule = (event, name) => {
        const isChecked = event.target.checked;
        this.setState({ [name]: isChecked });
    };

    render() {

        const { classes } = this.props;
        const { maxUsers, maxTriples, maxImportSize, queryTimeout, downloadTimeout, subscriptionVersion, treatContentAs, dataManager } = this.state;

        let importSizeInvalid = maxImportSize < MIN_IMPORT_SIZE || maxImportSize > MAX_IMPORT_SIZE;
        let queryTimeoutInvalid = queryTimeout < MIN_QUERY_TIMEOUT || queryTimeout > MAX_QUERY_TIMEOUT;
        let triplesInvalid = maxTriples < MIN_TRIPLES || maxTriples > MAX_TRIPLES;
        let downloadTimeoutInvalid = downloadTimeout < MIN_DOWNLOAD_TIMEOUT || downloadTimeout > MAX_DOWNLOAD_TIMEOUT;
        let usersInvalid = maxUsers < this.props.subscription.maxUsers || maxUsers > MAX_USERS;
        let invalid = importSizeInvalid || queryTimeoutInvalid || triplesInvalid || usersInvalid || downloadTimeoutInvalid;

        return (
            <React.Fragment>
                <Dialog className={classes.dialog}
                    open={true}
                    maxWidth="md"
                    fullWidth
                    onClose={this.exitUpdateSubscription} aria-labelledby="update-subscription-dialog-title">
                    <DialogTitle disableTypography className={classes.mainHeading} id="update-subscription-dialog-title">Update Subscription</DialogTitle>
                    <DialogContent>
                        <Grid container spacing={2} classes={
                            {
                                "spacing-xs-2": this.props.classes.fullGrid
                            }
                        }>
                            <Grid item xs={2}>
                                <TitledPanel height="full" title="Version">
                                    <TextField
                                        onChange={this.handleVersionChange}
                                        InputProps={{
                                            inputProps: {
                                                "aria-label": "Subscription version",
                                                "data-test": "update-version",
                                                "min": 0,
                                                "max": 5
                                            },
                                            className: classes.inputLabel
                                        }}
                                        variant="outlined"
                                        type="number"
                                        value={subscriptionVersion} />
                                </TitledPanel>
                            </Grid>
                            <Grid item xs={4}>
                                <TitledPanel height="full" title="Max Users">
                                    <TextField
                                        onChange={this.handleMaxUsersChange}
                                        InputProps={{
                                            inputProps: {
                                                "aria-label": "Maximum users",
                                                "data-test": "update-max-users",
                                                "min": this.props.subscription.maxUsers,
                                                "max": MAX_USERS,
                                                ...(usersInvalid) && {
                                                    "aria-invalid": true,
                                                    "aria-describedby": "users-helper-text",
                                                }
                                            },
                                            className: classes.inputLabel
                                        }}
                                        error={usersInvalid}
                                        helperText={usersInvalid ? <span id="users-helper-text" data-test="helper-text">Must be {this.props.subscription.maxUsers}-{MAX_USERS}</span> : null}
                                        variant="outlined"
                                        type="number"
                                        value={maxUsers} />
                                </TitledPanel>
                            </Grid>
                            <Grid item xs={4}></Grid>
                            <Grid item xs={6}>
                                <TitledPanel height="full" title="Max Triples">
                                    <TextField
                                        onChange={this.handleMaxTriplesChange}
                                        InputProps={{
                                            inputProps: {
                                                "aria-label": "Maximum triples",
                                                "data-test": "update-max-triples",
                                                "min": MIN_TRIPLES,
                                                "max": MAX_TRIPLES,
                                                ...(triplesInvalid) && {
                                                    "aria-invalid": true,
                                                    "aria-describedby": "triples-helper-text",
                                                }
                                            },
                                            className: classes.inputLabel
                                        }}
                                        error={triplesInvalid}
                                        helperText={triplesInvalid ? <span id="triples-helper-text" data-test="helper-text">Must be {MIN_TRIPLES}-{MAX_TRIPLES}</span> : null}
                                        variant="outlined"
                                        type="number"
                                        value={maxTriples} />
                                </TitledPanel>
                            </Grid>
                            <Grid item xs={6}>
                                <TitledPanel height="full" title="Max Import Size (MB)">
                                    <TextField
                                        onChange={this.handleMaxImportSizeChange}
                                        InputProps={{
                                            inputProps: {
                                                "aria-label": "Maximum import size (MB)",
                                                "data-test": "update-max-import-size",
                                                "min": MIN_IMPORT_SIZE,
                                                "max": MAX_IMPORT_SIZE,
                                                ...(importSizeInvalid) && {
                                                    "aria-invalid": true,
                                                    "aria-describedby": "import-helper-text",
                                                }
                                            },
                                            className: classes.inputLabel
                                        }}
                                        error={importSizeInvalid}
                                        helperText={importSizeInvalid ? <span id="import-helper-text" data-test="helper-text">Must be {MIN_IMPORT_SIZE}-{MAX_IMPORT_SIZE}</span> : null}
                                        variant="outlined"
                                        type="number"
                                        value={maxImportSize} />
                                </TitledPanel>
                            </Grid>
                            <Grid item xs={6}>
                                <TitledPanel height="full" title="Query Timeout (Seconds)">
                                    <TextField
                                        onChange={this.handleQueryTimeoutChange}
                                        InputProps={{
                                            inputProps: {
                                                "aria-label": "Query timeout (seconds)",
                                                "data-test": "update-query-timeout",
                                                "min": MIN_QUERY_TIMEOUT,
                                                "max": MAX_QUERY_TIMEOUT,
                                                ...(queryTimeoutInvalid) && {
                                                    "aria-invalid": true,
                                                    "aria-describedby": "query-helper-text",
                                                }
                                            },
                                            className: classes.inputLabel
                                        }}
                                        error={queryTimeoutInvalid}
                                        helperText={queryTimeoutInvalid ? <span id="query-helper-text" data-test="helper-text">Must be {MIN_QUERY_TIMEOUT}-{MAX_QUERY_TIMEOUT}</span> : null}
                                        variant="outlined"
                                        type="number"
                                        value={queryTimeout} />
                                </TitledPanel>
                            </Grid>
                            <Grid item xs={6}>
                                <TitledPanel height="full" title="Download Timeout (Seconds) (N-Triples)">
                                    <TextField
                                        onChange={this.handleDownloadTimeoutChange}
                                        InputProps={{
                                            inputProps: {
                                                "aria-label": "Download timeout (seconds)",
                                                "data-test": "update-download-timeout",
                                                "min": MIN_DOWNLOAD_TIMEOUT,
                                                "max": MAX_DOWNLOAD_TIMEOUT,
                                                ...(downloadTimeoutInvalid) && {
                                                    "aria-invalid": true,
                                                    "aria-describedby": "download-helper-text",
                                                }
                                            },
                                            className: classes.inputLabel
                                        }}
                                        error={downloadTimeoutInvalid}
                                        helperText={downloadTimeoutInvalid ? <span id="download-helper-text" data-test="helper-text">Must be {MIN_DOWNLOAD_TIMEOUT}-{MAX_DOWNLOAD_TIMEOUT}</span> : null}
                                        variant="outlined"
                                        type="number"
                                        value={downloadTimeout} />
                                </TitledPanel>
                            </Grid>
                            <Grid item xs={4}>
                                <div>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                data-test={"option-data-managers"}
                                                checked={dataManager}
                                                onChange={(e) => this.updateModule(e, "dataManager")}
                                                name="dataManager"
                                                classes={{ root: classes.compactCheckbox }}
                                            />
                                        }
                                        label="Data Manager"
                                        classes={{ label: classes.formLabelInlineLight }}
                                    />
                                </div>
                            </Grid>
                            <Grid item xs={4}>
                                <div>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                data-test={"option-treat-content-as"}
                                                checked={treatContentAs}
                                                onChange={(e) => this.updateModule(e, "treatContentAs")}
                                                name="treatContentAs"
                                                classes={{ root: classes.compactCheckbox }}
                                            />
                                        }
                                        label="Treat Content As"
                                        classes={{ label: classes.formLabelInlineLight }}
                                    />
                                </div>
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions className={classes.dialogActions}>
                        <CancelButton onClick={this.exitUpdateSubscription} />
                        <PrimaryButton disabled={invalid} onClick={this.updateSubscription}>Update</PrimaryButton>
                    </DialogActions>
                </Dialog>
            </React.Fragment>
        );
    }
}

UpdateSubscriptionDialog.propTypes = {
    classes: PropTypes.object,
    onUpdate: PropTypes.func.isRequired,
    subscription: PropTypes.object.isRequired,
    maxUsers: PropTypes.number,
    maxImportSize: PropTypes.number,
    queryTimeout: PropTypes.number,
    downloadTimeout: PropTypes.number,
    handlePrimaryChange: PropTypes.func,
    handleSecondaryChange: PropTypes.func,
    onCancel: PropTypes.func.isRequired
};

export default withStyles(styles)(UpdateSubscriptionDialog);